import React from 'react';
import {
  CollapsibleSection,
  DatePicker,
  DropdownField,
  TimePicker,
} from '@roger/ui-components';
import type {
  InfectionLogFormState,
  UpdateInfectionLogField,
} from './infectionLogForm';
import {
  InfectionFieldLabel,
  InfectionYesNoNaGroup,
} from './infectionLogFormFields';

interface InfectionActionsSectionProps {
  form: InfectionLogFormState;
  updateField: UpdateInfectionLogField;
  physicianOptions: { value: string; label: string }[];
}

const InfectionActionsSection: React.FC<InfectionActionsSectionProps> = ({
  form,
  updateField,
  physicianOptions,
}) => (
  <CollapsibleSection title='Actions Taken & Communication' defaultOpen>
    <div className='space-y-4'>
      {/* Physician Notified */}
      <div className='space-y-2'>
        <InfectionYesNoNaGroup
          label='Physician Notified?'
          name='physician_notified'
          value={form.physicianNotified}
          onChange={v => updateField('physicianNotified', v)}
        />
        {form.physicianNotified === 'yes' && (
          <div className='ml-6 space-y-2'>
            <div>
              <DropdownField
                label='Physician'
                htmlFor='infection-log-notified-physician'
                sortOptions
                placeholder='Select physician'
                searchPlaceholder='Search physicians...'
                enableFiltering
                isClearable={false}
                popoverWidth='min(28rem, 90vw)'
                options={physicianOptions}
                value={form.physicianNotifiedPhysicianId}
                onChange={v => updateField('physicianNotifiedPhysicianId', v)}
              />
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <InfectionFieldLabel>Date Notified</InfectionFieldLabel>
                <DatePicker
                  date={form.physicianNotifiedDate}
                  onDateChange={d => updateField('physicianNotifiedDate', d)}
                />
              </div>
              <div>
                <InfectionFieldLabel>Time</InfectionFieldLabel>
                <TimePicker
                  time={form.physicianNotifiedTime || undefined}
                  onTimeChange={time =>
                    updateField('physicianNotifiedTime', time ?? '')
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Labs Ordered */}
      <div className='space-y-2'>
        <InfectionYesNoNaGroup
          label='Labs Ordered?'
          name='labs_ordered'
          value={form.labsOrdered}
          onChange={v => updateField('labsOrdered', v)}
        />
        {form.labsOrdered === 'yes' && (
          <div className='ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2'>
            <div>
              <InfectionFieldLabel>Date Ordered</InfectionFieldLabel>
              <DatePicker
                date={form.labsOrderedDate}
                onDateChange={d => updateField('labsOrderedDate', d)}
              />
            </div>
            <div>
              <InfectionFieldLabel>Time</InfectionFieldLabel>
              <TimePicker
                time={form.labsOrderedTime || undefined}
                onTimeChange={time =>
                  updateField('labsOrderedTime', time ?? '')
                }
              />
            </div>
            <div className='sm:col-span-2'>
              <InfectionFieldLabel required>Notes</InfectionFieldLabel>
              <input
                type='text'
                maxLength={500}
                value={form.labsOrderedNotes}
                onChange={e => updateField('labsOrderedNotes', e.target.value)}
                className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
              />
            </div>
          </div>
        )}
      </div>

      {/* Patient Hospitalized */}
      <div className='space-y-2'>
        <InfectionYesNoNaGroup
          label='Patient Hospitalized?'
          name='patient_hospitalized'
          value={form.patientHospitalized}
          onChange={v => updateField('patientHospitalized', v)}
        />
        {form.patientHospitalized === 'yes' && (
          <div className='ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2'>
            <div>
              <InfectionFieldLabel>Date</InfectionFieldLabel>
              <DatePicker
                date={form.patientHospitalizedDate}
                onDateChange={d => updateField('patientHospitalizedDate', d)}
              />
            </div>
            <div>
              <InfectionFieldLabel>Time</InfectionFieldLabel>
              <TimePicker
                time={form.patientHospitalizedTime || undefined}
                onTimeChange={time =>
                  updateField('patientHospitalizedTime', time ?? '')
                }
              />
            </div>
            <div className='sm:col-span-2'>
              <InfectionFieldLabel>Notes</InfectionFieldLabel>
              <input
                type='text'
                maxLength={500}
                value={form.patientHospitalizedNotes}
                onChange={e =>
                  updateField('patientHospitalizedNotes', e.target.value)
                }
                className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
              />
            </div>
          </div>
        )}
      </div>

      {/* Culture Taken */}
      <div className='space-y-2'>
        <InfectionYesNoNaGroup
          label='Culture Taken?'
          name='culture_taken'
          value={form.cultureTaken}
          onChange={v => updateField('cultureTaken', v)}
        />
        {form.cultureTaken === 'yes' && (
          <div className='ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2'>
            <div>
              <InfectionFieldLabel>Date</InfectionFieldLabel>
              <DatePicker
                date={form.cultureTakenDate}
                onDateChange={d => updateField('cultureTakenDate', d)}
              />
            </div>
            <div>
              <InfectionFieldLabel>Time</InfectionFieldLabel>
              <TimePicker
                time={form.cultureTakenTime || undefined}
                onTimeChange={time =>
                  updateField('cultureTakenTime', time ?? '')
                }
              />
            </div>
            <div className='sm:col-span-2'>
              <InfectionFieldLabel required>Source</InfectionFieldLabel>
              <input
                type='text'
                maxLength={500}
                value={form.cultureSource}
                onChange={e => updateField('cultureSource', e.target.value)}
                className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
              />
            </div>
          </div>
        )}
      </div>

      {/* Treatment Notes (always visible) */}
      <div>
        <InfectionFieldLabel>Treatment Notes</InfectionFieldLabel>
        <textarea
          maxLength={2000}
          rows={3}
          value={form.treatmentNotes}
          onChange={e => updateField('treatmentNotes', e.target.value)}
          className='flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-placeholder focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none'
        />
      </div>
    </div>
  </CollapsibleSection>
);

export default InfectionActionsSection;
